import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useFootDetection } from '../../processing/detection';
import ImageStorageService from '../../storage/ImageStorageService';
import './VideoFeed.css';

function VideoFeed({ onCapture, isRecording }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const isMountedRef = useRef(true);

  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [imageIndex, setImageIndex] = useState(1);
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [selectedFoot, setSelectedFoot] = useState('left');

  const [description, setDescription] = useState('');
  const [capturedImages, setCapturedImages] = useState([]);
  const [lastCapture, setLastCapture] = useState(null);
  const [autoSave, setAutoSave] = useState(true);

  const {
    isLoading: isModelLoading,
    isReady: isModelReady,
    isRunning: isDetectionEnabled,
    results: detectionResults,
    initialize: initializeDetection,
    start: startDetection,
    stop: stopDetection,
    drawResults,
    adjustBoxSize,
    moveBox,
    clearManualBoxes
  } = useFootDetection({
    onError: (err) => console.error('Detection error:', err)
  });

  useEffect(() => {
    isMountedRef.current = true;

    const getDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = allDevices.filter(device => device.kind === 'videoinput');

        if (isMountedRef.current) {
          setDevices(videoDevices);
          if (videoDevices.length > 0 && !selectedDeviceId) {
            setSelectedDeviceId(videoDevices[0].deviceId);
          }
        }
      } catch (err) {
        console.error('Error getting video devices:', err);
        if (isMountedRef.current) {
          setError('Camera access denied');
        }
      }
    };

    getDevices();

    return () => {
      isMountedRef.current = false;
    };
  }, [selectedDeviceId]);

  const startStream = useCallback(async () => {
    if (!selectedDeviceId) {
      setError('No camera selected');
      return;
    }

    try {
      setError(null);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: selectedDeviceId },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        },
        audio: false
      });

      if (isMountedRef.current && videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      console.error('Error starting video stream:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Failed to start camera');
        setIsStreaming(false);
      }
    }
  }, [selectedDeviceId]);

  const stopStream = useCallback(() => {
    stopDetection();

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setIsStreaming(false);
    setShowAdjustments(false);
  }, [stopDetection]);

  const toggleDetection = async () => {
    if (isDetectionEnabled) {
      stopDetection();
      setShowAdjustments(false);
    } else {
      if (!isModelReady) {
        await initializeDetection();
      }
      startDetection(videoRef.current, canvasRef.current);
    }
  };

  const formatIndex = (num) => {
    return String(num).padStart(5, '0');
  };

  const captureFrame = useCallback((viewType) => {
    if (!videoRef.current || !isStreaming) return null;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);

    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    const filename = `${formatIndex(imageIndex)}_${viewType}.jpeg`;

    const captureData = {
      timestamp: Date.now(),
      image: imageData,
      filename: filename,
      viewType: viewType,
      index: imageIndex,
      description: description
    };

    ImageStorageService.addCapture(captureData);
    ImageStorageService.downloadImage(imageData, filename);

    setCapturedImages(prev => {
      const existing = prev.findIndex(img => img.filename === filename);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = captureData;
        return updated;
      }
      return [...prev, captureData];
    });

    setLastCapture(captureData);

    if (onCapture) {
      onCapture(captureData);
    }

    return captureData;
  }, [isStreaming, imageIndex, description, onCapture]);

  const handleDownloadCSV = () => {
    const success = ImageStorageService.downloadCSV(`labels_${Date.now()}.csv`);
    if (!success) {
      alert('No captures to export');
    }
  };

  const handleClearCaptures = () => {
    if (window.confirm('Clear all captured data?')) {
      setCapturedImages([]);
      ImageStorageService.clearCaptures();
      setLastCapture(null);
    }
  };

  const handleIndexChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = value === '' ? '' : parseInt(value, 10);
      setImageIndex(numValue);
    }
  };

  const handleIndexBlur = () => {
    if (imageIndex === '' || imageIndex < 1) {
      setImageIndex(1);
    }
  };

  const adjustIndex = (delta) => {
    setImageIndex(prev => {
      const currentVal = typeof prev === 'number' ? prev : 1;
      const newVal = currentVal + delta;
      return newVal >= 1 ? newVal : 1;
    });
  };

  const handleDeviceChange = (e) => {
    const newDeviceId = e.target.value;
    setSelectedDeviceId(newDeviceId);

    if (isStreaming) {
      stopStream();
      setTimeout(() => {
        if (isMountedRef.current) {
          startStream();
        }
      }, 100);
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const detectedFeet = detectionResults?.feet || [];

  return (
    <div className="video-feed-widget" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="video-feed-header">
        <h4>Video Feed</h4>
        <div className="header-badges">
          {isDetectionEnabled && (
            <span className="detection-badge">🦶 Detection ON</span>
          )}
          {isRecording && isStreaming && (
            <span className="video-recording-badge">● REC</span>
          )}
          {capturedImages.length > 0 && (
            <span className="capture-count-badge">
              📷 {capturedImages.length}
            </span>
          )}
        </div>
        {isStreaming && (
          <div className="detection-control-row">
            <button
              onClick={toggleDetection}
              className={`video-btn detection ${isDetectionEnabled ? 'active' : ''}`}
              disabled={isModelLoading}
            >
              {isModelLoading ? 'Loading Model...' :
              isDetectionEnabled ? 'Disable Detection' : 'Enable Foot Detection'}
            </button>

            {detectedFeet.length > 0 && (
              <div className="detection-info">
                {detectedFeet.map((foot, i) => (
                  <span key={foot.id || i} className={`foot-indicator ${foot.side} ${foot.isDefault ? 'uncertain' : ''}`}>
                    {foot.side}: {foot.confidence > 0 ? `${Math.round(foot.confidence * 100)}%` : '?'}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {devices.length > 1 && (
        <div className="device-selector-row">
          <select
            value={selectedDeviceId}
            onChange={handleDeviceChange}
            className="device-select"
            disabled={isStreaming}
          >
            {devices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Camera ${devices.indexOf(device) + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="control-row">
        <div className="index-control">
          {!isStreaming ? (
            <button onClick={startStream} className="video-btn start">
              Start Camera
            </button>
          ) : (
            <button onClick={stopStream} className="video-btn stop">
              Stop Camera
            </button>
          )}

          <label>Index:</label>
          <div className="index-input-group">
            <button
              className="index-btn"
              onClick={() => adjustIndex(-1)}
              disabled={imageIndex <= 1}
            >
              −
            </button>
            <input
              type="text"
              value={imageIndex}
              onChange={handleIndexChange}
              onBlur={handleIndexBlur}
              className="index-input"
              placeholder="1"
            />
            <button
              className="index-btn"
              onClick={() => adjustIndex(1)}
            >
              +
            </button>
          </div>
          <span className="index-preview">
            {formatIndex(imageIndex || 1)}_[View].jpeg
          </span>
        </div>
      </div>

      <div className="description-row">
        <label>Description / Labels:</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., knuckling, twisting, soft shank, dead"
          className="description-input"
          rows={2}
        />
      </div>

      {isStreaming && (
        <div className="capture-controls">
          <button
            onClick={() => captureFrame('Front')}
            className="video-btn capture front"
          >
            Front
          </button>
          <button
            onClick={() => captureFrame('Side')}
            className="video-btn capture side"
          >
            Side
          </button>
          <button
            onClick={() => captureFrame('Back')}
            className="video-btn capture back"
          >
            Back
          </button>
          <button
            onClick={() => captureFrame('Flat')}
            className="video-btn capture flat"
          >
            Flat
          </button>
        </div>
      )}

      <div className="video-container" style={{ flex: 1, minHeight: 0 }}>
        {error ? (
          <div className="video-error">
            <span>⚠️</span>
            <p>{error}</p>
          </div>
        ) : !isStreaming ? (
          <div className="video-placeholder">
            <span>📷</span>
            <p>Camera not active</p>
          </div>
        ) : null}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`video-element ${isStreaming ? 'active' : 'hidden'}`}
        />
        <canvas
          ref={canvasRef}
          className={`detection-canvas ${isStreaming && isDetectionEnabled ? 'active' : 'hidden'}`}
        />
      </div>

      {lastCapture && (
        <div className="last-capture-preview">
          <span className="preview-filename">{lastCapture.filename}</span>
          <img src={lastCapture.image} alt="Last capture" className="preview-image" />
        </div>
      )}

      {capturedImages.length > 0 && (
        <div className="export-section">
          <div className="export-header">
            <span>Captures: {capturedImages.length}</span>
            <div className="export-buttons">
              <button onClick={handleDownloadCSV} className="export-btn csv">
                📄 Download CSV
              </button>
              <button onClick={handleClearCaptures} className="clear-btn">
                🗑️ Clear
              </button>
            </div>
          </div>

          <div className="captures-list">
            {capturedImages.map((capture) => (
              <div key={capture.filename} className="capture-item">
                <span className="capture-index">{formatIndex(capture.index)}</span>
                <span className="capture-view">{capture.viewType}</span>
                <span className="capture-desc">{capture.description || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoFeed;
